import { useToast } from '@pancakeswap/uikit'
import { useCallback, useState } from 'react'
import { usePublicClient } from 'wagmi'
import { getWalletClient } from 'wagmi/actions'
import { liveGasParams } from 'utils/liveGas'
import { useContractBinding } from '../contract'
import { useV3Wallet } from '../hooks'

/**
 * Returns the transaction hash on success and false otherwise.
 * Sends one contract call from the connected wallet on BNB Smart Chain and waits for the receipt.
 * A wallet on another chain is asked to switch first; nothing is sent until it is on the bound contract's chain.
 */
export function useV3Tx() {
  const { chainId } = useContractBinding()
  const publicClient = usePublicClient({ chainId })
  const { isConnected, wrongChain, switchToBsc } = useV3Wallet()
  const { toastSuccess, toastError } = useToast()
  const [pending, setPending] = useState(false)
  const send = useCallback(
    async (label: string, req: any, opts?: { onHash?: (hash: string) => void; onError?: (message: string) => void }) => {
      if (!isConnected) {
        toastError('Wallet', 'Connect a wallet first')
        return false
      }
      setPending(true)
      try {
        if (wrongChain && !(await switchToBsc())) {
          toastError('Network', 'Switch your wallet to BNB Smart Chain to continue')
          return false
        }
        const wallet = await getWalletClient({ chainId })
        if (!wallet) throw new Error('Wallet is not ready on BNB Smart Chain')
        const hash = await wallet.writeContract({ ...req, ...(await liveGasParams(chainId)), chain: wallet.chain, account: wallet.account })
        opts?.onHash?.(hash) // the caller can remember a sent transaction before its receipt (no second send after a reload)
        const receipt = await publicClient.waitForTransactionReceipt({ hash })
        if (receipt.status !== 'success') throw new Error('Transaction reverted')
        toastSuccess(label, hash)
        return hash as string
      } catch (e: any) {
        const rejected = /reject|denied|cancel/i.test(String(e?.name || e?.shortMessage || e?.message || ''))
        const message = rejected ? 'Cancelled in the wallet' : String(e?.shortMessage || e?.message || e).slice(0, 200)
        toastError(label, message)
        opts?.onError?.(message) // a caller may also show it inline (the profile's referral withdraw)
        return false
      } finally {
        setPending(false)
      }
    },
    [isConnected, wrongChain, switchToBsc, publicClient, toastSuccess, toastError, chainId],
  )
  return { send, pending }
}
